import React, { useEffect, useState } from 'react'
import { ViewersProjectTableBodyLineProps } from './ViewersProjectTableBodyLine.types'
import styles from './ViewersProjectTableBodyLine.module.scss'
import cl from 'classnames'
import {
  Checkbox,
  ExpandButton,
  ImageSquare,
  TableBodyLine,
  TableCellProps,
  TableCellWithAdditionalCount,
  Text1,
  Text2
} from 'components/shared'
import { viewersTableWidths, getViewersTableFlexWidth } from '../ViewersTable.types'
import {
  STATISTICS_VIEWERS_TABLE_STORE, StatsTableEntity, StatsViewersTableSortProps
} from 'store'
import { formatDuration, formatPrice, getMainImageSrcForObj } from 'utilities'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { isColEntityProduct } from './ViewersProjectTableBodyLine.utils'
import { BestItem } from 'components/statistics/CirclePieSegment/BestItem'
import { StatsTableColType } from '../../../../../store/StatsBaseTableStore'
import { Grid } from "@material-ui/core"

const ViewersProjectsTableBodyLineComponent = (props: ViewersProjectTableBodyLineProps) => {
  const {
    className,
    style,
    tableEntity,
    mainEntitySubLevel = 0,
    [STATISTICS_VIEWERS_TABLE_STORE]: {
      isExpandedInteractionsColumn,
      isExpandedWatchTimeColumn,
      tableSettings,
      getSubTableEntity,
      onMainEntityCheckboxClick,
      isMainEntitySelected,
      getEntityGraphColor,
      onColumnEntityCheckboxClick,
      isCorrespondingEntitySelected,
      getSelectedCorrespondingEntitiesCount,
      isSomeCorrespondingEntitySelected,
      onColumnEntityAggregationCheckboxClick,
      isColumnMarked,
      addExpensesColLabel,
      tableSortingCompareFn,
      isTotalWatchTimeColDisabled
    }
  } = props

  const [expandedColumn, setExpandedColumn] = useState<StatsTableColType>(null)

  // reset expand if table settings change
  useEffect(() => setExpandedColumn(null), [tableSettings])

  const overviewProjectsTableFlexWidth =
    getViewersTableFlexWidth(tableSettings.draggableColumnsCount)

  const getDynamicTableCellProps = (
    tblEntity: StatsTableEntity
  ): TableCellProps[] => {
    const mainEntityHasChildren = Boolean(tblEntity.children?.length)

    const cells: TableCellProps[] = [
      {
        widthString: overviewProjectsTableFlexWidth,
        children: (
          <>
            {mainEntityHasChildren && (
              <ExpandButton
                isExpanded={expandedColumn === StatsTableColType.MAIN}
                onClick={() => setExpandedColumn(
                  s => s === StatsTableColType.MAIN
                    ? null
                    : StatsTableColType.MAIN
                )}
                style={{ marginLeft: mainEntitySubLevel * 25 }}
              />
            )}
            {(expandedColumn === StatsTableColType.MAIN ||
              Boolean(mainEntitySubLevel)) &&
              (
                <Checkbox
                  isChecked={isCorrespondingEntitySelected(
                    tableEntity.mainEntity.id,
                    tblEntity.mainEntity.id,
                    StatsTableColType.MAIN
                  )}
                  style={{
                    marginRight: 10,
                    marginLeft: (mainEntitySubLevel && !mainEntityHasChildren
                      ? mainEntitySubLevel * 25 + 2
                      : 0)
                  }}
                  onClick={() => onColumnEntityCheckboxClick(
                    tableEntity.mainEntity.id,
                    tblEntity.mainEntity.id,
                    StatsTableColType.MAIN
                  )}
                />
              )}

            {isColEntityProduct(tableSettings.mainEntityColLabel) && (
              <ImageSquare
                src={getMainImageSrcForObj(tblEntity.mainEntity)}
                width={30}
                showBorder
                style={{ marginRight: 12 }}
              />
            )}

            <Text1
              overflowEllipsis
              title={tblEntity.mainEntity.name}
            >
              {tblEntity.mainEntity.name}
            </Text1>
          </>
        ),
        style: {
          flex: 1,
          minWidth: viewersTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      },
      {
        widthString: overviewProjectsTableFlexWidth,
        children: tblEntity.primaryCorrespondingEntitiesCount > 1
          ? (
            <>
              <ExpandButton
                isExpanded={expandedColumn === StatsTableColType.PRIMARY}
                onClick={() => setExpandedColumn(
                  s => s === StatsTableColType.PRIMARY
                    ? null
                    : StatsTableColType.PRIMARY
                )}
              />
              {expandedColumn === StatsTableColType.PRIMARY && (
                <Checkbox
                  isChecked={isSomeCorrespondingEntitySelected(
                    tableEntity.mainEntity.id,
                    tblEntity.primaryCorrespondingEntities.map(e => e.id),
                    StatsTableColType.PRIMARY
                  )}
                  style={{ marginRight: 10 }}
                  onClick={() => onColumnEntityAggregationCheckboxClick(
                    tableEntity.mainEntity.id,
                    tblEntity.primaryCorrespondingEntities.map(e => e.id),
                    StatsTableColType.PRIMARY
                  )}
                />
              )}
              <Text2
                className={styles.textHighlighted}
              >
                {getSelectedCorrespondingEntitiesCount(
                  tableEntity.mainEntity.id,
                  tblEntity.primaryCorrespondingEntities.map(e => e.id),
                  StatsTableColType.PRIMARY
                )} selected
              </Text2>
              <Text2
                className={styles.textSecondary}
              >
                out of {tblEntity.primaryCorrespondingEntitiesCount}
              </Text2>
            </>
          )
          : (
            <>
              {isColEntityProduct(tableSettings.primaryCorrespondingEntityColLabel) && (
                <ImageSquare
                  src={getMainImageSrcForObj(tblEntity.primaryCorrespondingEntities?.length &&
                    tblEntity.primaryCorrespondingEntities[0])}
                  width={30}
                  showBorder
                  style={{ marginRight: 12 }}
                />
              )}
              <TableCellWithAdditionalCount
                text={tblEntity.primaryCorrespondingEntities?.length &&
                  tblEntity.primaryCorrespondingEntities[0]?.name}
                title={tblEntity.primaryCorrespondingEntities?.length &&
                  tblEntity.primaryCorrespondingEntities[0]?.name}
              />
            </>
          ),
        hoverComponent: tblEntity.primaryCorrespondingEntitiesCount > 1
          ? (
            <BestItem
              initialShowState
              positionFixed
              item={tblEntity.bestItemPrimary}
            />
          )
          : null,
        style: {
          flex: 1,
          minWidth: viewersTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      }
    ]

    if (tableSettings.draggableColumnsCount > 2) {
      cells.push({
        widthString: overviewProjectsTableFlexWidth,
        children: tblEntity.secondaryCorrespondingEntitiesCount > 1
          ? (
            <>
              <ExpandButton
                isExpanded={expandedColumn === StatsTableColType.SECONDARY}
                onClick={() => setExpandedColumn(
                  s => s === StatsTableColType.SECONDARY
                    ? null
                    : StatsTableColType.SECONDARY
                )}
              />
              {expandedColumn === StatsTableColType.SECONDARY && (
                <Checkbox
                  isChecked={isSomeCorrespondingEntitySelected(
                    tableEntity.mainEntity.id,
                    tblEntity.secondaryCorrespondingEntities.map(e => e.id),
                    StatsTableColType.SECONDARY
                  )}
                  style={{ marginRight: 10 }}
                  onClick={() => onColumnEntityAggregationCheckboxClick(
                    tableEntity.mainEntity.id,
                    tblEntity.secondaryCorrespondingEntities.map(e => e.id),
                    StatsTableColType.SECONDARY
                  )}
                />
              )}
              <Text2
                className={styles.textHighlighted}
              >
                {getSelectedCorrespondingEntitiesCount(
                  tableEntity.mainEntity.id,
                  tblEntity.secondaryCorrespondingEntities.map(e => e.id),
                  StatsTableColType.SECONDARY
                )} selected
              </Text2>
              <Text2
                className={styles.textSecondary}
              >
                out of {tblEntity.secondaryCorrespondingEntitiesCount}
              </Text2>
            </>
          )
          : (
            <>
              {isColEntityProduct(tableSettings.secondaryCorrespondingEntityColLabel) && (
                <ImageSquare
                  src={
                    getMainImageSrcForObj(tblEntity.secondaryCorrespondingEntities?.length &&
                      tblEntity.secondaryCorrespondingEntities[0])}
                  width={30}
                  showBorder
                  style={{ marginRight: 12 }}
                />
              )}
              <TableCellWithAdditionalCount
                text={tblEntity.secondaryCorrespondingEntities?.length &&
                  tblEntity.secondaryCorrespondingEntities[0]?.name}
                title={tblEntity.secondaryCorrespondingEntities?.length &&
                  tblEntity.secondaryCorrespondingEntities[0]?.name}
              />
            </>
          ),
        hoverComponent: tblEntity.secondaryCorrespondingEntitiesCount > 1
          ? (
            <BestItem
              initialShowState
              positionFixed
              item={tblEntity.bestItemSecondary}
            />
          )
          : null,
        style: {
          flex: 1,
          minWidth: viewersTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      })
    }

    return cells
  }

  const getStatsTableCellProps = (
    tableEntity: StatsTableEntity,
    isSubTableEntity?: boolean
  ): TableCellProps[] => {
    const cells = [
      {
        widthPx: isExpandedWatchTimeColumn 
        ? viewersTableWidths.expandedTabs 
        : viewersTableWidths.tabs,
        children: (
        <Grid container spacing={1} className={styles.gridContainer}>
        {isExpandedWatchTimeColumn && <>
          <Grid>
            <div 
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}>{isSubTableEntity ? '-' : formatDuration(tableEntity.stats.totalWatch)}
            </div>
          </Grid>
          <Grid>
            <div 
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}>{isSubTableEntity ? '-' : formatDuration(tableEntity.stats.totalVovWatch)}
            </div>
          </Grid>
          </>}
          <Grid>
            <div 
            style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
            className={styles.paper}>{isSubTableEntity ? '-' : formatDuration(tableEntity.stats.totalWatch + tableEntity.stats.totalVovWatch)}
            </div>
          </Grid>
        </Grid>
        ),
        isColumnMarked: isColumnMarked(StatsViewersTableSortProps.TOTAL_WATCH),
        forceHideContent: isTotalWatchTimeColDisabled
      },
      {
        widthPx: isExpandedInteractionsColumn 
        ? viewersTableWidths.expandedTotalInteractionTime 
        : viewersTableWidths.totalInteractionTime,
        children: (
      <Grid container spacing={1} className={styles.gridContainer}>
       {isExpandedInteractionsColumn && <>
        <Grid>
          <div 
          style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
          className={styles.paper}>{isSubTableEntity ? '-' : formatDuration(tableEntity.stats.totalInteraction)}
           </div>
        </Grid>
        <Grid>
          <div 
          style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
          className={styles.paper}>{isSubTableEntity ? '-' : formatDuration(tableEntity.stats.totalPageInteraction)}
           </div>
        </Grid> 
        <Grid>
          <div 
          style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
          className={styles.paper}>{isSubTableEntity ? '-' : formatDuration(tableEntity.stats.totalVovInteraction)}
           </div>
        </Grid> 
          </>}
        <Grid>
          <div 
          style={{ fontWeight: Boolean(!isSubTableEntity && expandedColumn) ? 'bolder': 'normal'}}
          className={styles.paper}>{isSubTableEntity ? '-' 
          : formatDuration(tableEntity.stats.totalInteraction
          + tableEntity.stats.totalPageInteraction 
          + tableEntity.stats.totalVovInteraction)}
           </div>
        </Grid>
      </Grid>
        ),
        isColumnMarked: isColumnMarked(StatsViewersTableSortProps.TOTAL_INTERACTION)
      },
      {
        widthPx: viewersTableWidths.tabs,
        children: (
          <Text1
            bold={Boolean(!isSubTableEntity && expandedColumn)}
          >
            {formatPrice(tableEntity.stats.addExpenses)}
          </Text1>
        ),
        isColumnMarked: isColumnMarked(addExpensesColLabel)
      },
      {
        widthPx: viewersTableWidths.tabs,
        children: (
          <Text1
            bold
            style={{
              color: tableEntity.stats.purchases === 0
                ? styles.error
                : undefined
            }}
          >
            {formatPrice(tableEntity.stats.purchases)}
          </Text1>
        ),
        isColumnMarked: isColumnMarked(StatsViewersTableSortProps.PURCHASE_REVENUE)
      }
    ]

    return cells
  }

  return (
    <div
      className={cl(styles.container, {
        [styles.expandedZone]: expandedColumn && !mainEntitySubLevel,
        [styles.noBackgroundColor]: expandedColumn || mainEntitySubLevel
      }, className)}
      style={style}
    >
      <TableBodyLine
        cells={[
          {
            children: mainEntitySubLevel
              ? null
              : (
                <Checkbox
                  isChecked={isMainEntitySelected(tableEntity.mainEntity.id)}
                  fillColor={getEntityGraphColor(tableEntity.mainEntity.id)}
                  onClick={() => onMainEntityCheckboxClick(tableEntity.mainEntity.id)}
                />
              ),
            widthPx: viewersTableWidths.selector
          },
          ...getDynamicTableCellProps(tableEntity),
          ...getStatsTableCellProps(tableEntity)
        ]}
        containerProps={{
          style: {
            width: 'unset'
          }
        }} // For Proper table formatting
      />
      {expandedColumn === StatsTableColType.PRIMARY &&
        tableEntity.primaryCorrespondingEntities
          .map(entityObject => getSubTableEntity(
            tableEntity.mainEntity,
            entityObject,
            expandedColumn
          ))
          .sort(tableSortingCompareFn)
          .map(subTableEntity => {
            const entityObject = subTableEntity.primaryCorrespondingEntities[0]
            return (
              <TableBodyLine
                key={entityObject.id}
                cells={[
                  {
                    widthPx: viewersTableWidths.selector
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: viewersTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    }
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: viewersTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    },
                    children: (
                      <>
                        <Checkbox
                          isChecked={isCorrespondingEntitySelected(
                            tableEntity.mainEntity.id,
                            entityObject.id,
                            expandedColumn
                          )}
                          style={{ marginLeft: 28 }}
                          onClick={() => onColumnEntityCheckboxClick(
                            tableEntity.mainEntity.id,
                            entityObject.id,
                            expandedColumn
                          )}
                        />
                        {
                          isColEntityProduct(
                            tableSettings.primaryCorrespondingEntityColLabel
                          ) && (
                            <ImageSquare
                              src={getMainImageSrcForObj(entityObject)}
                              width={30}
                              showBorder
                              style={{ marginLeft: 12 }}
                            />
                          )
                        }
                        <Text1
                          overflowEllipsis
                          style={{ marginLeft: 10 }}
                          title={entityObject.name}
                        >
                          {entityObject.name}
                        </Text1>
                      </>
                    )
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: viewersTableWidths.draggableColMinWidth,
                      maxWidth: 'unset',
                      display: tableSettings.draggableColumnsCount > 2 ? undefined : 'none'
                    }
                  },
                  ...getStatsTableCellProps(subTableEntity, true)
                ]}
              />
            )
          })}
      {expandedColumn === StatsTableColType.SECONDARY &&
        tableEntity.secondaryCorrespondingEntities
          .map(entityObject => getSubTableEntity(
            tableEntity.mainEntity,
            entityObject,
            expandedColumn
          ))
          .sort(tableSortingCompareFn)
          .map(subTableEntity => {
            const entityObject = subTableEntity.secondaryCorrespondingEntities[0]
            return (
              <TableBodyLine
                key={entityObject.id}
                cells={[
                  {
                    widthPx: viewersTableWidths.selector
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: viewersTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    }
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: viewersTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    }
                  },
                  {
                    widthString: overviewProjectsTableFlexWidth,
                    style: {
                      flex: 1,
                      minWidth: viewersTableWidths.draggableColMinWidth,
                      maxWidth: 'unset'
                    },
                    children: (
                      <>
                        <Checkbox
                          isChecked={isCorrespondingEntitySelected(
                            tableEntity.mainEntity.id,
                            entityObject.id,
                            expandedColumn
                          )}
                          style={{ marginLeft: 28 }}
                          onClick={() => onColumnEntityCheckboxClick(
                            tableEntity.mainEntity.id,
                            entityObject.id,
                            expandedColumn
                          )}
                        />
                        {
                          isColEntityProduct(
                            tableSettings.secondaryCorrespondingEntityColLabel
                          ) && (
                            <ImageSquare
                              src={getMainImageSrcForObj(entityObject)}
                              width={30}
                              showBorder
                              style={{ marginLeft: 12 }}
                            />
                          )
                        }
                        <Text1
                          overflowEllipsis
                          style={{ marginLeft: 10 }}
                          title={entityObject.name}
                        >
                          {entityObject.name}
                        </Text1>
                      </>
                    )
                  },
                  ...getStatsTableCellProps(subTableEntity, true)
                ]}
              />
            )
          })}

      {expandedColumn === StatsTableColType.MAIN &&
        tableEntity.children
          .sort(tableSortingCompareFn)
          .map((tEntity, index) => (
            <ViewersProjectsTableBodyLine
              key={index}
              tableEntity={tEntity}
              mainEntitySubLevel={mainEntitySubLevel + 1}
            />
          ))}
    </div>
  )
}

const ViewersProjectsTableBodyLine = inject(
  STATISTICS_VIEWERS_TABLE_STORE
)(observer(ViewersProjectsTableBodyLineComponent))
export { ViewersProjectsTableBodyLine }
